import { useEffect, useState } from "react";
import { FiEye, FiClock, FiMail } from "react-icons/fi";
import { formatDateTime } from "../utils/formatDate";
import Table from "../components/Table";
import EmailHistorySnapshotModal from "../components/EmailHistorySnapshotModal";
import { fetchEmailLogs } from "../api/contacts";
import { useAuth } from "../context/AuthContext";

export default function EmailHistory() {
  const { token } = useAuth();

  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  const pageSize = 50;

  // 🔹 Fetch email logs
  useEffect(() => {
    const loadLogs = async () => {
      if (!token?.access) return;
      setLoading(true);
      setError("");

      try {
        const data = await fetchEmailLogs(token.access, page, pageSize);
        setLogs(data.results || []);
        setCount(data.count || 0);
        setTotalPages(data.total_pages || Math.ceil((data.count || 0) / pageSize) || 1);
      } catch (err) {
        console.error("Failed to load email logs:", err.message);
        setError("Failed to load email logs.");
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [page, token]);

  const columns = [
    {
      key: "contact_name",
      header: "Contact",
      render: (row) => (
        <div>
          <span className="font-medium text-gray-900 block">
            {row.contact_name || "Unknown"}
          </span>
          {row.contact_company && (
            <span className="text-xs text-gray-500 block truncate max-w-[180px]">
              {row.contact_company}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "contact_email",
      header: "Email",
      render: (row) => (
        <span className="text-gray-700 font-mono text-xs">
          {row.contact_email}
        </span>
      ),
    },
    {
      key: "subject",
      header: "Subject Sent",
      render: (row) => (
        <span className="text-gray-800 line-clamp-1 max-w-xs font-medium" title={row.subject}>
          {row.subject || <span className="text-gray-400 italic">No subject</span>}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const colorMap = {
          Sent: "bg-green-100 text-green-700 border border-green-200",
          Opened: "bg-blue-100 text-blue-700 border border-blue-200",
          Failed: "bg-red-100 text-red-700 border border-red-200",
          Pending: "bg-yellow-100 text-yellow-700 border border-yellow-200",
        };

        const colorClass =
          colorMap[row.status] || "bg-gray-100 text-gray-700 border border-gray-200";

        return (
          <span
            className={`px-2.5 py-1 text-xs font-semibold rounded-full ${colorClass}`}
          >
            {row.status}
          </span>
        );
      },
    },
    {
      key: "sent_at",
      header: "Sent At",
      render: (row) => (
        <span className="text-xs text-gray-600">
          {row.sent_at ? formatDateTime(row.sent_at) : "-"}
        </span>
      ),
    },
    {
      key: "opened_at",
      header: "Opened At",
      render: (row) => (
        <span className="text-xs text-blue-600 font-medium">
          {row.opened_at ? formatDateTime(row.opened_at) : "-"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Action",
      render: (row) => (
        <button
          onClick={() => setSelectedLog(row)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold text-xs rounded-lg transition border border-blue-200 shadow-2xs cursor-pointer"
          title="View sent email snapshot"
        >
          <FiEye size={13} />
          <span>View Email</span>
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiClock className="text-blue-600" /> Email History ({count})
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Exact historical snapshots of all personalized emails sent to contacts
          </p>
        </div>
      </div>

      {loading ? (
        <div className="p-12 flex flex-col items-center justify-center text-gray-500">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent mb-3"></div>
          <p className="text-sm font-medium">Loading email history...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-center">
          {error}
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center max-w-md mx-auto">
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
            <FiMail size={24} />
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1">No emails sent yet</h3>
          <p className="text-xs text-gray-500">
            When you send emails through outreach campaigns or quick send, their full sent snapshots will appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
            <Table columns={columns} data={logs} />
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                className="px-3.5 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 transition cursor-pointer"
                onClick={() => setPage((prev) => prev - 1)}
                disabled={page === 1}
              >
                Prev
              </button>
              <span className="text-sm font-medium text-gray-600 px-2">
                Page {page} of {totalPages}
              </span>
              <button
                className="px-3.5 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 transition cursor-pointer"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Sent Email Snapshot Modal */}
      {selectedLog && (
        <EmailHistorySnapshotModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
}

